import { AppService } from './../app.service';
import { Injectable } from '@angular/core';
import {
  DataArgs,
  BondData,
  CalculateArgsWithId,
  CalculatedResult,
  Subheader,
  CouponDate,
  CalculateArgsWithoutId
} from './bond-calculator.model';
// import { Observable } from 'rxjs';
import { VwdOption } from '@vwd/vwd-pilldown';
import * as moment from 'moment';
import { SettingsBox } from './settings-box/settings-box.model';
import { toDateOrDefault } from '@vwd/ngx-i18n';
import { ResultBox, CashFlowItem, CashFlowData } from './result-box/result-box.model';
import { CalcBox } from './calc-box/calc-box.model';
import { Observable, from as fromPromise } from 'rxjs';


@Injectable()
export class BondCalculatorService {

  private financeApi: any;

  constructor(private appService: AppService) {
    this.financeApi = this.appService.getInstance();
  }

  getData(instrumentId: number, params: DataArgs): Observable<BondData> {
    return fromPromise(this._getData(instrumentId, params));
  }

  private _getData(instrumentId: number, params: DataArgs): Promise<BondData> {

    return this.financeApi.find({
      service: 'bond.search',
      source: 'SW',
      param: {
        instrumentId,
        addSecurityObject: 1,
        addSnapQuoteObject: 1,
        addExchangeObject: 1
      },
      fieldList: {
        securityObject: [
          'instrumentId',
          'isin',
          'firstCouponDate',
          'descriptionShort',
          'maturityDate',
          'issueDate',
          'interestRate',
          'redemptionPrice',
          'numberInterestRatesPayedPA',
          'wpk',
          'ticker'
        ],
        snapQuoteObject: [
          'lastPrice',
          'netchangePercent',
          'netchangeValue',
          'lastPriceTimestamp',
          'lastPriceQuality',
          'transformedPermissioningStatus',
          'cumulativeVolume',
          'ratingBidPrice',
          'ratingAskPrice',
          'ratingBidPriceTimestamp'
        ],
        exchangeObject: [
          'exchangeName'
        ],
      }

    }).then((result: any) => {

      if (result.length === 0) {
        const myError: any = new Error('Data not found');
        myError.code = 'FREQUINCES_NOT_FOUND';

        throw myError;
      }

      return result.map(async (row: any) => {
        const mySecurity: any = {
          default: {
            securityObject: {},
            snapQuoteObject: {},
            exchangeObject: {}
          }
        };

        row.forEach((element: any) => {
          const [namespace, objectName] = element.objectName.split('.');
          if (mySecurity[namespace] && mySecurity[namespace][objectName]) {
            mySecurity[namespace][objectName] = element.data;
          }
        });
        const bondWmCouponData: VwdOption[] = await this.bondWmCouponData(mySecurity.default.securityObject.instrumentId);
        const paramsToCalculate = new CalculateArgsWithId(
          mySecurity.default.securityObject.firstCouponDate,
          mySecurity.default.securityObject.interestRate,
          mySecurity.default.securityObject.maturityDate,
          mySecurity.default.securityObject.redemptionPrice,
          moment(params.valueDate).format('YYYY-MM-DD'),
          mySecurity.default.securityObject.issueDate,
          bondWmCouponData[0].value,
          mySecurity.default.snapQuoteObject.lastPrice,
          params.expectedYield,
          params.taxRate,
          params.nominalVolume,
          mySecurity.default.securityObject.numberInterestRatesPayedPA,
          params.dayCountConvention,
          params.calculationMethod,
          mySecurity.default.securityObject.instrumentId
        );

        const paramsToCalculateBid = { ...paramsToCalculate, cleanPrice: mySecurity.default.snapQuoteObject.ratingBidPrice };
        const paramsToCalculateAsk = { ...paramsToCalculate, cleanPrice: mySecurity.default.snapQuoteObject.ratingAskPrice };

        const calculatedResult: CalculatedResult = await this._getCalculatedResult(paramsToCalculate);
        const calculatedResultBid: CalculatedResult = await this._getCalculatedResult(paramsToCalculateBid);
        const calculatedResultAsk: CalculatedResult = await this._getCalculatedResult(paramsToCalculateAsk);

        const totalAccurateInterest: number = (((calculatedResult.accruedInterest * 1000) * (params.nominalVolume / 100000)) * -1);

        return new BondData(
          new SettingsBox(
            mySecurity.default.securityObject.interestRate,
            // toDateOrDefault(mySecurity.default.securityObject.issueDate),
            // toDateOrDefault(mySecurity.default.securityObject.firstCouponDate),
            new Date(mySecurity.default.securityObject.issueDate),
            new Date(mySecurity.default.securityObject.firstCouponDate),
            bondWmCouponData.length ? bondWmCouponData[0].value : null,
            new Date(mySecurity.default.securityObject.maturityDate),
            mySecurity.default.securityObject.redemptionPrice,
            mySecurity.default.securityObject.couponFrequency,
            bondWmCouponData
          ),
          new ResultBox(
            +calculatedResult.dirtyPrice,
            +calculatedResult.cleanPrice,
            +calculatedResult.modifiedDuration,
            +calculatedResult.expectedYield,
            +calculatedResultBid.expectedYield,
            +calculatedResultAsk.expectedYield,
            calculatedResult.duration,
            calculatedResult.errorMessage,
            calculatedResult.errorCode,
            +calculatedResult.grossYield,
            +calculatedResult.netYield,
            calculatedResult.accruedInterest,
            calculatedResult.basisPointValue,
            calculatedResult.convexity,
            calculatedResult.maturity,
            calculatedResult.accruedInterestDays,
            totalAccurateInterest,
            calculatedResult.cashflowItem,
            mySecurity.default.snapQuoteObject.ratingBidPrice,
            mySecurity.default.snapQuoteObject.ratingAskPrice,
            // toDateOrDefault(mySecurity.default.snapQuoteObject.ratingBidPriceTimestamp),
            new Date(mySecurity.default.snapQuoteObject.ratingBidPriceTimestamp),
            false
          ),
          new Subheader(
            mySecurity.default.securityObject.ticker,
            mySecurity.default.securityObject.isin,
            mySecurity.default.securityObject.wpk,
            mySecurity.default.snapQuoteObject.netchangePercent,
            mySecurity.default.snapQuoteObject.netchangeValue,
            mySecurity.default.snapQuoteObject.lastPrice,
            mySecurity.default.exchangeObject.exchangeName,
            // toDateOrDefault(mySecurity.default.snapQuoteObject.lastPriceTimestamp)
            new Date(mySecurity.default.snapQuoteObject.lastPriceTimestamp)
          ),
          new CalcBox(calculatedResult.expectedYield, calculatedResult.cleanPrice),
        );

      })[0];
    });
  }

  bondWmCouponData(instrumentId: number): Promise<VwdOption[]> {

    return this.financeApi.find({
      service: 'bond.getBondWmCouponData',
      source: 'SW',
      param: {
        instrumentId,
      }
    }).then((result: any) => {
      if (result.length === 0) {
        const myError: any = new Error('Lastcoupons not found');
        myError.code = 'LAST_COUPONS_NOT_FOUND';

        throw myError;
      }

      const lastCouponDateArr: VwdOption[] = result.data.map((row: CouponDate) => {
        return new VwdOption(moment(row.couponDate).format('DD.MM.YYYY'), moment(row.couponDate).format('YYYY-MM-DD'));
      });

      return lastCouponDateArr.reverse();

    });
  }

  getCalculatedResult(params: CalculateArgsWithId): Observable<CalculatedResult> {
    return fromPromise(this._getCalculatedResult(params));
  }

  private _getCalculatedResult(params: CalculateArgsWithId): Promise<CalculatedResult> {

    return this.financeApi.find({
      service: 'bond.calculate',
      source: 'SW',
      param: params
    }).then((result: any) => {

      if (result.length === 0) {
        const myError: any = new Error('Calculated Result not found');
        myError.code = 'CALCULATED_RESULT_NOT_FOUND';

        throw myError;
      }
      const cashFlowItem: CashFlowData[] = this.prepareCaschflow(result.data.cashflowItem);

      return new CalculatedResult(
        +result.data.dirtyPrice,
        + result.data.cleanPrice,
        result.data.modifiedDuration,
        // tslint:disable-next-line:no-string-literal
        +result.data['yield'], // because yield is reserved word in JavaScript
        result.data.duration,
        result.data.errorMessage,
        result.data.errorCode,
        +result.data.grossYield,
        +result.data.netYield,
        result.data.accruedInterest,
        result.data.basisPointValue,
        result.data.convexity,
        result.data.maturity,
        result.data.accruedInterestDays,
        cashFlowItem);
    });

  }

  prepareCaschflow(cashflowItems: CashFlowData[]): CashFlowData[] {

    const cashFlowData: CashFlowData[] = [];

    cashflowItems.forEach((cashflowItem: CashFlowData) => {
      cashFlowData.push(new CashFlowData(
        typeof cashflowItem.cashValue === 'string' ? +cashflowItem.cashValue : cashflowItem.cashValue,
        cashflowItem.date,
        typeof cashflowItem.couponFactor === 'string' ? +cashflowItem.couponFactor : cashflowItem.couponFactor,
        typeof cashflowItem.presentValue === 'string' ? +cashflowItem.presentValue : cashflowItem.presentValue,
        cashflowItem.timePeriod)
      );
    });

    return cashFlowData;
  }

  simulateCalculation(params: CalculateArgsWithoutId | CalculateArgsWithId): Observable<ResultBox> {
    return fromPromise(this._simulateCalculation(params));
  }

  private _simulateCalculation(params: CalculateArgsWithoutId | CalculateArgsWithId): Promise<ResultBox> {

    const instrumentId = params.instrumentId || null;

    return this.financeApi.find({
      service: 'bond.calculate',
      source: 'SW',
      param: params
    }).then((result: any) => {

      if (result.length === 0) {
        const myError: any = new Error('Simulation result not found');
        myError.code = 'SIMULATION_RESULT_NOT_FOUND';
        throw myError;
      }

      const totalAccurateInterest = (((result.data.accruedInterest * 1000) * (+params.nominalVolume / 100000)) * -1);
      const cashFlowItem: CashFlowData[] = this.prepareCaschflow(result.data.cashflowItem);

      return new ResultBox(
        +result.data.dirtyPrice,
        +result.data.cleanPrice,
        result.data.modifiedDuration,
        // tslint:disable-next-line:no-string-literal
        +result.data['yield'],
        0,
        0, // because yield is reserved word in JavaScript
        result.data.duration,
        result.data.errorMessage,
        result.data.errorCode,
        +result.data.grossYield,
        +result.data.netYield,
        result.data.accruedInterest,
        result.data.basisPointValue,
        result.data.convexity,
        result.data.maturity,
        result.data.accruedInterestDays,
        totalAccurateInterest,
        cashFlowItem,
        0,
        0,
        null,
        instrumentId ? false : true
      );
    });
  }
}
